import { MilestonesGridData } from '../types';
import { useFilterMilestones } from './useFilterMilestones';
import { useFilterByProductGroup } from './useFilterByProductGroup';
import { useFilterByAudience } from './useFilterByAudience';
import { useFilterByCategoryProduct } from './useFilterByCategoryProduct';
import { useFilterBySurfaces } from './useFilterBySurfaces';
import { useFilterByGenAi } from './useFilterByGenAi';
import { useFilterByStatus } from './useFilterByStatus';
import { useFilterByRevenueImpact } from './useFilterByRevenueImpact';
import { useFilterByMomentType } from './useFilterByMomentType';
import { useFilterByMustWin } from './useFilterByMustWin';

/**
 * Consolidated hook that applies all filters to milestones data with AND logic
 * Filters are applied sequentially in the following order:
 * 1. Lanes/Tiers
 * 2. Product Groups
 * 3. Customer Audiences
 * 4. Categories and Products (OR within)
 * 5. Surfaces
 * 6. Gen AI
 * 7. Status/Risk Level
 * 8. Revenue Impact
 * 9. Moment Type
 * 10. Must Win
 */
export const useFilteredMilestones = (
  rawMilestonesData: MilestonesGridData | null
): MilestonesGridData | null => {
  // Apply filters to the data with AND logic (chain filters sequentially)
  // Phase 1: Filter by Lanes/Tiers
  const tierFilteredData = useFilterMilestones(rawMilestonesData);

  // Phase 2: Filter by Product Groups (applied on tier-filtered data for AND logic)
  const productGroupFilteredData = useFilterByProductGroup(tierFilteredData);

  // Phase 3: Filter by Customer Audiences (applied on product-group-filtered data for AND logic)
  const audienceFilteredData = useFilterByAudience(productGroupFilteredData);

  // Phase 4: Filter by Categories and Products (applied on audience-filtered data for AND logic)
  const categoryProductFilteredData =
    useFilterByCategoryProduct(audienceFilteredData);

  // Phase 5: Filter by Surfaces (applied on category-product-filtered data for AND logic)
  const surfacesFilteredData = useFilterBySurfaces(categoryProductFilteredData);

  // Phase 6: Filter by Gen AI (applied on surfaces-filtered data for AND logic)
  const genAiFilteredData = useFilterByGenAi(surfacesFilteredData);

  // Phase 7: Filter by Status/Risk Level (applied on gen-ai-filtered data for AND logic)
  const statusFilteredData = useFilterByStatus(genAiFilteredData);

  // Phase 8: Filter by Revenue Impact (applied on status-filtered data for AND logic)
  const revenueImpactFilteredData =
    useFilterByRevenueImpact(statusFilteredData);

  // Phase 9: Filter by Moment Type (roadmap parity)
  const milestonesData = useFilterByMomentType(revenueImpactFilteredData);

  // // Phase 10: Filter by Must Win (applied on moment-type-filtered data for AND logic)
  // const milestonesData = useFilterByMustWin(momentTypeFilteredData);

  return milestonesData;
};
